import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Shield, Star, Zap, Heart, ChevronRight } from 'lucide-react';

export default function About() {
  return (
    <>
      <Helmet>
        <title>Meist | Tõuks.ee — Eesti trikitõukside spetsialist</title>
        <meta name="description" content="Tõuks.ee on Eesti trikitõukside spetsialist. Müüme Blunt, Aztek, Drone ja teisi tippmärke. Asjatundlik nõustamine, originaaltooted, tasuta saatmine." />
        <link rel="canonical" href="https://xn--tuks-0qa.ee/meist" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'Meist — Tõuks.ee',
          url: 'https://xn--tuks-0qa.ee/meist',
          description: 'Tõuks.ee on Eesti trikitõukside ja varuosade spetsialistpood',
          mainEntity: {
            '@type': 'Organization',
            name: 'Tõuks.ee / Kvantis OÜ',
            url: 'https://xn--tuks-0qa.ee',
            logo: 'https://res.cloudinary.com/daka2lob2/image/upload/v1751272742/Touks_logo3_ed9w6g.png',
            email: 'ametliktouks@gmail.com',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Suislepa tn 2',
              addressLocality: 'Jõgeva',
              addressCountry: 'EE',
            },
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Kodu', item: 'https://xn--tuks-0qa.ee/' },
            { '@type': 'ListItem', position: 2, name: 'Meist', item: 'https://xn--tuks-0qa.ee/meist' },
          ],
        })}</script>
      </Helmet>

      <div className="container mx-auto py-12 md:py-20 px-4 md:px-6">
        {/* Hero */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Eesti trikitõukside spetsialist</h1>
          <p className="text-gray-600 text-lg leading-relaxed">
            Tõuks.ee sündis lihtsa ideega — tuua Eestisse maailma parimad trikitõuksid ja varuosad, 
            koos asjatundliku nõustamise ja kiire kohaletoimetamisega.
          </p>
        </div>

        {/* Story */}
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 mb-16 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4">Meie lugu</h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Tõuks.ee on Kvantis OÜ e-pood, mis on pühendunud trikitõukside ja varuosade 
                müümisele Eestis. Meid ajendas asjaolu, et Eesti turul puudus spetsialistpood, 
                kus saaks osta originaalseid maailmatasemel tõukseid koos kompetentse nõustamisega.
              </p>
              <p>
                Meie valikus on maailma juhtivate brändide tooted — Blunt Scooters, Aztek, Drone, 
                North Scooters, Lucky Scooters ja teised tippmargid. Kõik meie tooted on 100% 
                originaalid, mis pärinevad ametlikelt edasimüüjatelt.
              </p>
              <p>
                Oleme siin selleks, et aidata nii esimest tõuksi valivat last kui ka kogenud 
                pro-sõitjat, kes otsib järgmist custom-ehituse komponenti.
              </p>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img
              src="https://res.cloudinary.com/daka2lob2/image/upload/v1751273983/UGC1.1_epveov.jpg"
              alt="Trikitõuks sõitja"
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>
        </div>

        {/* Values */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center mb-10">Miks Tõuks.ee?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                icon: <Shield className="w-6 h-6 text-[#4FC1E9]" />,
                title: '100% originaaltooted',
                desc: 'Kõik meie tooted on originaalid ametlikelt edasimüüjatelt. Tootja garantii kehtib kõikidele toodetele.',
              },
              {
                icon: <Star className="w-6 h-6 text-[#4FC1E9]" />,
                title: 'Tippmärgid ühest kohast',
                desc: 'Blunt, Aztek, Drone, North, Lucky — kõik maailma juhtivad trikitõukside brändid on meie valikus.',
              },
              {
                icon: <Zap className="w-6 h-6 text-[#4FC1E9]" />,
                title: 'Kiire kohaletoimetamine',
                desc: 'Tasuta saatmine DPD, Omniva ja SmartPost pakiautomaatidesse üle kogu Eesti. Laos olevad tooted saadame 1–2 tööpäevaga.',
              },
              {
                icon: <Heart className="w-6 h-6 text-[#4FC1E9]" />,
                title: 'Asjatundlik nõustamine',
                desc: 'Pole kindel, milline tõuks sobib? Kirjuta meile — aitame leida õige tõuksi, talla, lenksu või muu osa.',
              },
            ].map((v, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex gap-4">
                <div className="bg-[#4FC1E9]/10 rounded-xl p-3 flex-shrink-0 h-fit">{v.icon}</div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { to: '/toukeratas', label: 'Vaata tooteid', sub: 'Suur valik tõukseid ja varuosi' },
            { to: '/kontakt', label: 'Võta ühendust', sub: 'Vastame 1 tööpäeva jooksul' },
            { to: '/kohaletoimetamine', label: 'Saatmisinfo', sub: 'Tasuta kohaletoimetamine' },
          ].map(({ to, label, sub }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center justify-between bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:border-[#4FC1E9] transition-colors group"
            >
              <div>
                <p className="font-semibold text-gray-800 group-hover:text-[#4FC1E9] transition-colors">{label}</p>
                <p className="text-gray-500 text-sm mt-1">{sub}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#4FC1E9] transition-colors flex-shrink-0" />
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
